import { doc, setDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, FieldLabel, LoadingView, PrimaryButton, SBCard } from "@/components/ui";
import { db } from "@/firebase/app";
import { useDoc } from "@/hooks/firestoreHooks";
import { DEFAULT_PRIMARY_HEX, DEFAULT_SECONDARY_HEX, isValidHex, normalizedHex } from "@/models/appearance";
import { leagueFirestoreData, parseLeague } from "@/models/catalog";
import { humanActionAuditActor, recordActionAudit } from "@/services/actionAudit";
import { useActiveLeagueID } from "@/features/home/useLeagueSnapshot";
import "@/features/home/home.css";
import "@/features/league/league.css";

// Port of LeagueSettingsView. Rules (validLeagueAdminMetadataUpdate) allow
// admins to change only name, abbreviation, and the brand colors.

export function LeagueSettings() {
  const { currentUser } = useAuth();
  const { leagueID } = useActiveLeagueID();
  const league = useDoc(leagueID ? doc(db, "leagues", leagueID) : null, parseLeague);

  const [name, setName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loadedID, setLoadedID] = useState<string | null>(null);

  useEffect(() => {
    if (league.data && league.data.id !== loadedID) {
      setName(league.data.name);
      setAbbreviation(league.data.abbreviation);
      setPrimaryColor(league.data.primaryColorHex ?? "");
      setSecondaryColor(league.data.secondaryColorHex ?? "");
      setLoadedID(league.data.id);
    }
  }, [league.data, loadedID]);

  const save = async () => {
    if (!league.data) return;
    setError(null);
    setMessage(null);
    if (!name.trim()) {
      setError("League name is required.");
      return;
    }
    if ((primaryColor && !isValidHex(primaryColor)) || (secondaryColor && !isValidHex(secondaryColor))) {
      setError(`Colors must be 6-digit hex values, like #${DEFAULT_PRIMARY_HEX}.`);
      return;
    }
    setIsSaving(true);
    try {
      const next = {
        ...league.data,
        name: name.trim(),
        abbreviation: abbreviation.trim(),
        primaryColorHex: primaryColor ? normalizedHex(primaryColor) : undefined,
        secondaryColorHex: secondaryColor ? normalizedHex(secondaryColor) : undefined,
        updatedAt: new Date(),
      };
      await setDoc(doc(db, "leagues", league.data.id), leagueFirestoreData(next));
      if (currentUser) {
        void recordActionAudit({
          before: leagueFirestoreData(league.data),
          after: leagueFirestoreData(next),
          context: {
            leagueID: next.id,
            targetCollection: "leagues",
            targetDocumentID: next.id,
            targetDisplayName: next.name,
            actionType: "update",
            actor: humanActionAuditActor(currentUser),
          },
        });
      }
      setMessage("League settings saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setIsSaving(false);
    }
  };

  if (league.loading && !league.data) return <LoadingView />;
  if (!league.data) return <ErrorText>League could not be loaded.</ErrorText>;

  return (
    <div className="list-screen league-screen">
      <div className="list-screen-head">
        <h1>League Settings</h1>
      </div>
      <SBCard className="home-card">
        <FieldLabel label="League name">
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </FieldLabel>
        <FieldLabel label="Abbreviation">
          <input value={abbreviation} onChange={(e) => setAbbreviation(e.target.value)} />
        </FieldLabel>
        <div className="league-form-grid">
          <FieldLabel label="Primary color">
            <div className="league-color-control">
              <input
                type="color"
                value={isValidHex(primaryColor) ? `#${normalizedHex(primaryColor)}` : `#${DEFAULT_PRIMARY_HEX}`}
                onChange={(e) => setPrimaryColor(e.target.value)}
                aria-label="Choose primary color"
              />
              <input aria-label="Primary color hex" value={primaryColor} placeholder={`#${DEFAULT_PRIMARY_HEX}`} onChange={(e) => setPrimaryColor(e.target.value)} />
            </div>
          </FieldLabel>
          <FieldLabel label="Secondary color">
            <div className="league-color-control">
              <input
                type="color"
                value={isValidHex(secondaryColor) ? `#${normalizedHex(secondaryColor)}` : `#${DEFAULT_SECONDARY_HEX}`}
                onChange={(e) => setSecondaryColor(e.target.value)}
                aria-label="Choose secondary color"
              />
              <input aria-label="Secondary color hex" value={secondaryColor} placeholder={`#${DEFAULT_SECONDARY_HEX}`} onChange={(e) => setSecondaryColor(e.target.value)} />
            </div>
          </FieldLabel>
        </div>
        <PrimaryButton title={isSaving ? "Saving…" : "Save settings"} onClick={() => void save()} disabled={isSaving} />
        <ErrorText>{error}</ErrorText>
        {message && <p className="home-hint">{message}</p>}
      </SBCard>
    </div>
  );
}
